import { Inject, Injectable, LOCALE_ID, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { DialogActions, OrganizationState } from '@app/_store';
import { Store } from '@ngxs/store';
import { CookieService } from 'ngx-cookie-service';
import { of, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UiServices implements OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  private localeToCurrencyMap: Record<string, string> = {
    sv: 'SEK',
  };

  constructor(
    private store: Store,
    private cookieService: CookieService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    const cookieValue = this.cookieService.get('sub-participant-enable-info');
    UiServices.shouldShowSubParticipantInfo = !(cookieValue?.length > 0);
  }

  static shouldShowSubParticipantInfo: boolean;

  public subParticipantInfoEnabling(enabled: boolean) {
    const organization = this.store.selectSnapshot(OrganizationState.current);

    if (!organization.subParticipantsEnabled) return;
    if (!UiServices.shouldShowSubParticipantInfo) return;

    if (!enabled) return;

    this.store
      .dispatch(
        new DialogActions.Confirm(
          'Underdeltagare',
          `Detta val innebär att du måste konfigurera aktivitetens produkter/avgifter (en eller flera) med 'Avgiftsval' så att de kan väljas av deltagarna själva.<br/><br/>Främst används detta vid försäljning men det fungerar såklart på alla typer av aktiviteter där deltagarna kan välja produkter och man vill sprida möjligheten till personer utanför organisationen.`,
          () => {
            this.cookieService.set('sub-participant-enable-info', 'confirmed', 365, '/');
            UiServices.shouldShowSubParticipantInfo = false;
            return of(null);
          },
          false,
          'Okej, jag förstår'
        )
      )
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  getCurrencySymbol(locale: string = this.locale): string | undefined {
    const currencyCode = this.localeToCurrencyMap[locale];
    if (!currencyCode) {
      console.warn(`No currency code found for locale: ${locale}`);
      return undefined;
    }

    try {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
      });
      const parts = formatter.formatToParts(0);
      const currencyPart = parts.find(part => part.type === 'currency');
      return currencyPart?.value;
    } catch (error) {
      console.error(`Error getting currency symbol: ${error}`);
      return undefined;
    }
  }

  downloadFile(content: BlobPart, fileName: string, contentType: string) {
    // It is necessary to create a new blob object with mime-type explicitly set
    // otherwise only Chrome works like it should
    const newBlob = new Blob([content], { type: contentType });

    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const navigator = window.navigator as any;
    if (window.navigator && navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(newBlob, fileName);
      return;
    }

    // For other browsers:
    // Create a link pointing to the ObjectURL containing the blob.
    const data = window.URL.createObjectURL(newBlob);

    const link = document.createElement('a');
    link.href = data;
    link.download = fileName;
    // this is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );

    setTimeout(function () {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
      link.remove();
    }, 100);
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}

@Injectable()
export class PreviousRouteService {
  private previousUrl: string;
  private currentUrl: string;

  constructor(private router: Router) {}

  startMonitor() {
    this.router.events.subscribe(event => {
      this.currentUrl = this.router.url;
      if (event instanceof NavigationStart) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
  }

  public getPreviousUrl() {
    return this.previousUrl;
  }

  public getLastSuccessfulNavigation() {
    return this.router.lastSuccessfulNavigation();
  }
}
